import React, { useMemo } from 'react';
import {
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import FiltersSidebar, {
  FilterSection,
  FilterCheckboxRow,
  FilterCheckboxList,
} from './FiltersSidebar';
import type { CustomerKind } from '../services/customerService';
import type { Order } from '../services/orderService';
import type {
  UseOrderFiltersResult,
  BooqableStatus,
  PaymentStatus,
} from '../hooks/useOrderFilters';

interface OrderFiltersSidebarProps {
  filters: UseOrderFiltersResult;
  /** Source list — used to derive per-status / per-payment-status counts shown
   *  in the right column of each checkbox row. Counts are over the full loaded
   *  set, not the post-filter set, so they stay stable as you toggle. */
  orders: Order[];
  /** Customer-kind lookup. Same map the page passes to filters.applyFilters
   *  so the kind counts here match the filter's resolution rule. */
  customerKindById?: Map<string, CustomerKind>;
  collapsed: boolean;
}

// Date inputs use plain HTML <input type="date"> for v1 — Fluent doesn't
// ship a date picker in the v9 component set we're on. Keep the styling
// minimal so they read as part of the sidebar surface.
const useStyles = makeStyles({
  dateRow: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('6px'),
  },
  dateLabel: {
    fontSize: '11px',
    fontWeight: 500,
    color: tokens.colorNeutralForeground3,
  },
  dateInput: {
    width: '100%',
    boxSizing: 'border-box',
    ...shorthands.padding('6px', '8px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius('4px'),
    fontSize: '12px',
    fontFamily: 'inherit',
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    '&:focus': {
      outline: 'none',
      ...shorthands.borderColor(tokens.colorBrandStroke1),
    },
  },
});

// Order status — lifecycle minus `new`. Booqable creates orders in `new` but
// the first user-facing state is `draft`; surfacing `new` as a filter option
// confused staff since it overlapped with rows in transit and never cleared.
const STATUS_OPTIONS: { value: BooqableStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'reserved', label: 'Reserved' },
  { value: 'started', label: 'Started' },
  { value: 'stopped', label: 'Completed' },
  { value: 'archived', label: 'Archived' },
  { value: 'canceled', label: 'Canceled' },
];

// Payment-status filter options match the order mirror's vocabulary (#3) so a
// partially-paid or overpaid order is filterable, not bucketed under a stale
// "pending"/"overdue" label that the mirror never emits.
const PAYMENT_OPTIONS: { value: PaymentStatus; label: string }[] = [
  { value: 'payment_due', label: 'Payment Due' },
  { value: 'partially_paid', label: 'Partially Paid' },
  { value: 'paid', label: 'Paid' },
  { value: 'overpaid', label: 'Overpaid' },
];

// Contacts are sub-records of a Company and can't carry orders directly
// (orders bind to the parent Company), so the picker only offers Individual
// and Company. Filter state still uses the broader CustomerKind enum to
// match useOrderFilters / customerService.
const KIND_OPTIONS: { value: CustomerKind; label: string }[] = [
  { value: 'individual', label: 'Individual' },
  { value: 'company', label: 'Company' },
];

// Tags Booqable uses internally for shared-tenant env classification — staff
// never manage these, so they're suppressed from the filter's tag list (same
// rule OrderRightSidebar applies to the editable chip set). Mirrors the
// backend re-injection so SANDBOX never surfaces as a user-facing tag.
const ORDER_SYSTEM_TAGS = new Set(['SANDBOX']);

/**
 * Left-rail filter panel for the Orders table. Replaces the prior status
 * tabs — status now lives alongside the other dimensions in this panel.
 * Live-applied (no Apply button); state persists via useOrderFilters.
 */
const OrderFiltersSidebar: React.FC<OrderFiltersSidebarProps> = ({
  filters,
  orders,
  customerKindById,
  collapsed,
}) => {
  const styles = useStyles();
  const { state, setField, clearAll, activeFilterCount, hasActiveFilters } = filters;

  // Per-value counts. One pass over orders builds all three maps so the sidebar
  // re-derives in O(n) when the order list changes. Customer-kind count uses
  // the same lookup the page passes to filters.applyFilters so the numbers
  // match the filter's resolution rule (fallback to 'individual' when unknown).
  const { statusCounts, paymentCounts, kindCounts, tagCounts, tagOptions } = useMemo(() => {
    const s = new Map<BooqableStatus, number>();
    const p = new Map<PaymentStatus, number>();
    const k = new Map<CustomerKind, number>();
    const t = new Map<string, number>();
    for (const o of orders) {
      s.set(o.booqableStatus, (s.get(o.booqableStatus) || 0) + 1);
      p.set(o.paymentStatus, (p.get(o.paymentStatus) || 0) + 1);
      const kind = (o.customerId && customerKindById?.get(o.customerId)) || 'individual';
      k.set(kind, (k.get(kind) || 0) + 1);
      for (const tag of o.tagList || []) {
        if (ORDER_SYSTEM_TAGS.has(tag)) continue;
        t.set(tag, (t.get(tag) || 0) + 1);
      }
    }
    // Dynamic, alphabetically-sorted unique tag list across the loaded orders.
    const opts = Array.from(t.keys()).sort((a, b) => a.localeCompare(b));
    return {
      statusCounts: s,
      paymentCounts: p,
      kindCounts: k,
      tagCounts: t,
      tagOptions: opts,
    };
  }, [orders, customerKindById]);

  const toggleStatus = (s: BooqableStatus) => {
    const has = state.statuses.includes(s);
    setField(
      'statuses',
      has ? state.statuses.filter((x) => x !== s) : [...state.statuses, s],
    );
  };

  const togglePayment = (p: PaymentStatus) => {
    const has = state.paymentStatuses.includes(p);
    setField(
      'paymentStatuses',
      has ? state.paymentStatuses.filter((x) => x !== p) : [...state.paymentStatuses, p],
    );
  };

  const toggleKind = (k: CustomerKind) => {
    const has = state.customerKinds.includes(k);
    setField(
      'customerKinds',
      has ? state.customerKinds.filter((x) => x !== k) : [...state.customerKinds, k],
    );
  };

  const toggleTag = (tag: string) => {
    const has = state.tags.includes(tag);
    setField(
      'tags',
      has ? state.tags.filter((x) => x !== tag) : [...state.tags, tag],
    );
  };

  return (
    <FiltersSidebar
      collapsed={collapsed}
      activeFilterCount={activeFilterCount}
      hasActiveFilters={hasActiveFilters}
      onClearAll={clearAll}
      onRequestClose={() => filters.setField('sidebarCollapsed', true)}
    >
      <FilterSection label="Status">
        <FilterCheckboxList>
          {STATUS_OPTIONS.map((opt) => (
            <FilterCheckboxRow
              key={opt.value}
              checked={state.statuses.includes(opt.value)}
              onChange={() => toggleStatus(opt.value)}
              label={opt.label}
              count={statusCounts.get(opt.value) ?? 0}
            />
          ))}
        </FilterCheckboxList>
      </FilterSection>

      <FilterSection label="Payment status">
        <FilterCheckboxList>
          {PAYMENT_OPTIONS.map((opt) => (
            <FilterCheckboxRow
              key={opt.value}
              checked={state.paymentStatuses.includes(opt.value)}
              onChange={() => togglePayment(opt.value)}
              label={opt.label}
              count={paymentCounts.get(opt.value) ?? 0}
            />
          ))}
        </FilterCheckboxList>
      </FilterSection>

      <FilterSection label="Date range">
        <div className={styles.dateRow}>
          <span className={styles.dateLabel}>From</span>
          <input
            type="date"
            className={styles.dateInput}
            value={state.dateFrom}
            onChange={(e) => setField('dateFrom', e.target.value)}
          />
        </div>
        <div className={styles.dateRow}>
          <span className={styles.dateLabel}>Until</span>
          <input
            type="date"
            className={styles.dateInput}
            value={state.dateUntil}
            onChange={(e) => setField('dateUntil', e.target.value)}
          />
        </div>
      </FilterSection>

      <FilterSection label="Customer type">
        <FilterCheckboxList>
          {KIND_OPTIONS.map((opt) => (
            <FilterCheckboxRow
              key={opt.value}
              checked={state.customerKinds.includes(opt.value)}
              onChange={() => toggleKind(opt.value)}
              label={opt.label}
              count={kindCounts.get(opt.value) ?? 0}
            />
          ))}
        </FilterCheckboxList>
      </FilterSection>

      {/* Tags — derived dynamically from the tags present across loaded orders
          (e.g. "web-order"). System tags like SANDBOX are suppressed. Hidden
          entirely when no user-facing tags exist so the panel stays clean. */}
      {tagOptions.length > 0 && (
        <FilterSection label="Tags">
          <FilterCheckboxList>
            {tagOptions.map((tag) => (
              <FilterCheckboxRow
                key={tag}
                checked={state.tags.includes(tag)}
                onChange={() => toggleTag(tag)}
                label={tag}
                count={tagCounts.get(tag) ?? 0}
              />
            ))}
          </FilterCheckboxList>
        </FilterSection>
      )}
    </FiltersSidebar>
  );
};

export default OrderFiltersSidebar;
